import { type QueryClient } from '@tanstack/react-query'

import { assessmentProcessService } from '@/services/api/assessment_process.service'
import type { AssessmentProcessTree } from '@/types/assessment_process.types'

export interface AssessmentProcessQueryKeyFactory {
  assessmentProcess: {
    all: () => readonly ['assessmentProcess']
    tree: (assessmentId: string) => readonly ['assessmentProcess', 'tree', string]
    canAdvance: (assessmentId: string) => readonly ['assessmentProcess', 'canAdvance', string]
  }
}

export const assessmentProcessQueryKeys: AssessmentProcessQueryKeyFactory = {
  assessmentProcess: {
    all: () => ['assessmentProcess'] as const,
    tree: (assessmentId) => ['assessmentProcess', 'tree', assessmentId] as const,
    canAdvance: (assessmentId) => ['assessmentProcess', 'canAdvance', assessmentId] as const
  }
}

export interface AssessmentProcessQueryUtils {
  invalidateAssessmentProcessQueries: (
    queryClient: QueryClient,
    assessmentId?: string
  ) => Promise<void>
  setAssessmentProcessTreeOptimistic: (
    queryClient: QueryClient,
    assessmentId: string,
    data: Partial<AssessmentProcessTree>
  ) => void
  removeAssessmentProcessFromCache: (queryClient: QueryClient, assessmentId: string) => void
  prefetchAssessmentProcessTree: (queryClient: QueryClient, assessmentId: string) => Promise<void>
}

export const assessmentProcessQueryUtils: AssessmentProcessQueryUtils = {
  async invalidateAssessmentProcessQueries(queryClient, assessmentId) {
    const promises = [
      queryClient.invalidateQueries({
        queryKey: assessmentProcessQueryKeys.assessmentProcess.all()
      })
    ]

    if (assessmentId) {
      promises.push(
        queryClient.invalidateQueries({
          queryKey: assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId)
        }),
        queryClient.invalidateQueries({
          queryKey: assessmentProcessQueryKeys.assessmentProcess.canAdvance(assessmentId)
        })
      )
    }

    await Promise.all(promises)
  },

  async prefetchAssessmentProcessTree(queryClient, assessmentId) {
    await queryClient.prefetchQuery({
      queryKey: assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId),
      queryFn: () => assessmentProcessService.getAssessmentProcessById(assessmentId),
      staleTime: 1000 * 60 * 5 // 5 minutes
    })
  },

  setAssessmentProcessTreeOptimistic(queryClient, assessmentId, data) {
    // Update assessment process tree cache
    queryClient.setQueryData(
      assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId),
      (old: AssessmentProcessTree | undefined) => {
        if (!old) return old
        return { ...old, ...data }
      }
    )
  },

  removeAssessmentProcessFromCache(queryClient, assessmentId) {
    // Remove process tree from cache
    queryClient.removeQueries({
      queryKey: assessmentProcessQueryKeys.assessmentProcess.tree(assessmentId)
    })

    // Remove workflow related queries
    queryClient.removeQueries({
      queryKey: assessmentProcessQueryKeys.assessmentProcess.canAdvance(assessmentId)
    })
  }
}
